import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { DisplayablePrice } from './utils/useDisplayablePriceList';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { toEuros } from 'i18n';

const useStyles = createUseStyles((theme: Theme) => ({
  priceBlock: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
  },
  priceBlockMultiple: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.marginBase,
  },
  priceLabel: {
    ...theme.fonts.h4,
    color: theme.colors.inkPrimary,
  },
  price: {
    ...theme.fonts.h3,
    color: theme.colors.inkPrimary,
    marginRight: theme.marginBase,
  },
  priceMultiple: {
    ...theme.fonts.h4,
  },
}));

interface Props {
  priceObj: DisplayablePrice;
  multiplePrices?: boolean;
  showNoTax?: boolean;
  first?: boolean;
  last?: boolean;
  advantageAmount?: number;
}

export const PriceBlock = ({
  priceObj,
  multiplePrices,
  showNoTax,
  first,
  last,
  advantageAmount,
}: Props) => {
  const classes = useStyles();
  const { i18n, t } = useTranslation();

  const isLabel = 'label' in priceObj;
  const extraLines = (
    <>
      {advantageAmount
        ? !!last && (
            <p>
              {t('billing.offers.block.advantageAmount', {
                count: advantageAmount,
              })}
            </p>
          )
        : ((priceObj.extraCredits !== undefined && isLabel) ||
            !!priceObj.extraCredits) && (
            <p>
              {t('billing.offers.block.extraCredits', {
                count: priceObj.extraCredits,
              })}
            </p>
          )}
    </>
  );

  if (isLabel) {
    return (
      <div className={classes.priceBlock} key={priceObj.label}>
        <div className={classes.price}>
          {t(`billing.offers.block.labels.${priceObj.label}`)}
        </div>
        {extraLines}
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.priceBlock, {
        [classes.priceBlockMultiple]: multiplePrices,
      })}
      key={priceObj.unit}
    >
      {!first ? '+ ' : ''}
      <strong
        className={classNames(classes.price, {
          [classes.priceMultiple]: multiplePrices,
        })}
      >
        {toEuros(priceObj.price, true, i18n.language)}
      </strong>
      {showNoTax && `${t('billing.offers.block.noTax')} `}
      {i18n.exists(`billing.offers.block.units.${priceObj.unit}`) &&
        t(`billing.offers.block.units.${priceObj.unit}`)}
      {extraLines}
    </div>
  );
};
